// ============================================================================
// SIZE-ASSIST-01 — parcel-informed equipment sizing (pure math, no React).
//
// Two jobs:
//  1. decodeEquipSize(): pull the capacity out of an HVAC model number —
//     A/C + heat-pump tonnage rides in twelfths-of-a-ton codes (036 = 3 ton),
//     furnace input rides in kBTU codes (080 = 80,000 BTU in). Crude on
//     purpose: ~35% of our own equipment mirror decodes with these rules
//     (measured 2026-10-08), and a wrong guess is worse than no guess.
//  2. buildSizing(): county parcel facts (finished sqft + year built) ×
//     Minnesota-fixed design temp (~ -13°F) era factors → a suggested size,
//     cross-checked against what's already in the house. The existing unit is
//     an UPPER BOUND — old installs ran chronically oversized, so we never
//     suggest bigger than what's there without saying so out loud.
//
// The suggestion prices the LIKELY size. The load calc (pegasus_job.load_calc)
// stays the close gate — that line is non-negotiable in the UI copy.
// ============================================================================

export type SizeFamily = "cooling" | "heating";

export interface DecodedSize {
  family: SizeFamily;
  /** cooling only — nominal tons (1.5 … 5) */
  tons: number | null;
  /** heating only — input kBTU (40 … 140) */
  inputKbtu: number | null;
  /** the raw 3-digit code that matched, for receipts */
  token: string;
}

// Tonnage codes in twelfths (1.5t … 5t). 060 also reads as a 60k furnace —
// that collision is resolved by the family hint, never guessed.
const COOL_CODES = new Set(["018", "024", "030", "036", "042", "048", "060"]);
// Furnace input kBTU codes seen across Lennox / Trane / Carrier / Goodman / Rheem.
const HEAT_CODES = new Set([
  "040", "044", "045", "050", "060", "066", "070", "075", "080", "088", "090",
  "100", "105", "110", "115", "120", "125", "132", "135", "140",
]);

/** Distinct codes from `codes` that sit in `set`, first-seen order. */
function famMatches(codes: string[], set: Set<string>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of codes) if (set.has(c) && !seen.has(c)) { seen.add(c); out.push(c); }
  return out;
}

/**
 * Decode a model number into a size. `hint` routes the ambiguous codes:
 * pass "cooling" for A/C / heat-pump / coil slots, "heating" for furnace
 * slots. With no hint, a code that could be either (060) returns null —
 * honesty beats a coin flip.
 *
 * Two passes: standalone 3-digit tokens first (most reliable — Lennox
 * "ML14XC1-036"), then a sliding window over longer digit runs (Goodman
 * "GSX160241" → 024, Trane "4TTR6048" → 048, Rheem "R96VA0702317" → 070).
 * In either pass, two DISTINCT plausible codes for the same family = null.
 */
export function decodeEquipSize(model: string | null | undefined, hint?: SizeFamily | null): DecodedSize | null {
  if (!model) return null;
  const m = String(model).toUpperCase();
  const runs = m.match(/\d+/g) ?? [];
  const standalone = runs.filter((r) => r.length === 3);
  const embedded: string[] = [];
  for (const r of runs) {
    if (r.length > 3) for (let i = 0; i + 3 <= r.length; i++) embedded.push(r.slice(i, i + 3));
  }

  const resolve = (codes: string[]): DecodedSize | null => {
    if (codes.length === 0) return null;
    const cool = famMatches(codes, COOL_CODES);
    const heat = famMatches(codes, HEAT_CODES);
    if (hint === "cooling") {
      return cool.length === 1 ? { family: "cooling", tons: Number(cool[0]) / 12, inputKbtu: null, token: cool[0] } : null;
    }
    if (hint === "heating") {
      return heat.length === 1 ? { family: "heating", tons: null, inputKbtu: Number(heat[0]), token: heat[0] } : null;
    }
    // No hint: only answer when the evidence is one-sided.
    const coolOnly = cool.filter((c) => !HEAT_CODES.has(c));
    const heatOnly = heat.filter((c) => !COOL_CODES.has(c));
    if (coolOnly.length === 1 && heatOnly.length === 0) {
      return { family: "cooling", tons: Number(coolOnly[0]) / 12, inputKbtu: null, token: coolOnly[0] };
    }
    if (heatOnly.length === 1 && coolOnly.length === 0) {
      return { family: "heating", tons: null, inputKbtu: Number(heatOnly[0]), token: heatOnly[0] };
    }
    return null;
  };

  return resolve(standalone) ?? resolve(embedded);
}

/** Slot/label text → which size family that slot prices. */
export function familyHint(text: string | null | undefined): SizeFamily | null {
  if (!text) return null;
  const t = text.toLowerCase();
  if (/furnace|boiler|garage heater/.test(t)) return "heating";
  if (/a\/?c\b|air cond|condens|heat pump|mini.?split|rooftop|coil|air handler/.test(t)) return "cooling";
  return null;
}

// ---------------------------------------------------------------------------
// The sizing brain
// ---------------------------------------------------------------------------

export interface ParcelFacts {
  fin_sqft: number | null;
  year_built: number | null;
  city_norm: string | null;
  zip: string | null;
  county: string;
  use_class: string | null;
  dwelling_type: string | null;
  home_style: string | null;
  heating: string | null;
  cooling: string | null;
  num_units: number | null;
}

export interface ExistingSizes {
  coolingTons: number | null;
  coolingModel: string | null;
  heatInputKbtu: number | null;
  heatModel: string | null;
}

export interface SizingSuggestion {
  coolingTons: number | null;
  heatInputKbtu: number | null;
  confidence: "solid" | "rough";
  /** plain-English lines for the card */
  reasons: string[];
  /** amber lines — things a human must weigh (the `detail` of each caution; logged as-is) */
  flags: string[];
  /** The same cautions as a short label for the card chip + the sentence for the (i) (DESIGN-QUIET-02). */
  cautions: { label: string; detail: string }[];
  inputs: {
    sqft: number | null;
    yearBuilt: number | null;
    parcelMatched: boolean;
    existingCoolingTons: number | null;
    existingHeatInputKbtu: number | null;
  };
}

// Heating OUTPUT BTU per sqft at MN design temp (~ -13°F), by construction era.
// Old = leaky + uninsulated; new = tight envelope.
function heatFactor(year: number | null): number {
  if (year == null) return 35;
  if (year < 1950) return 45;
  if (year < 1980) return 40;
  if (year < 2000) return 35;
  if (year < 2015) return 30;
  return 25;
}
// Cooling sqft per ton, same era logic.
function coolSqftPerTon(year: number | null): number {
  if (year == null) return 900;
  if (year < 1950) return 700;
  if (year < 1980) return 800;
  if (year < 2000) return 900;
  return 1000;
}

const FURNACE_BUCKETS = [40, 60, 80, 100, 120];

/**
 * Parcel facts + existing equipment → suggestion. Returns null only when
 * NOTHING is known (no parcel match and no decodable existing unit).
 */
export function buildSizing(parcel: ParcelFacts | null, existing: ExistingSizes): SizingSuggestion | null {
  const reasons: string[] = [];
  const cautions: { label: string; detail: string }[] = [];
  const caution = (label: string, detail: string) => { cautions.push({ label, detail }); };

  let sqft = parcel?.fin_sqft ?? null;
  const year = parcel?.year_built ?? null;

  // Parcel sqft covers the whole BUILDING — useless for one unit of a multi.
  const multi = parcel != null && (parcel.num_units ?? 1) > 1;
  const nonRes = parcel != null && /apart|commer|indust/i.test(parcel.use_class ?? "");
  if (multi || nonRes) {
    if (multi) caution(`County: ${parcel!.num_units} units`, `County lists ${parcel!.num_units} units at this address — building sqft can't size one unit. Load calc required.`);
    else caution(`Use class: ${parcel!.use_class}`, `County use class is "${parcel!.use_class}" — not a single-family record. Treat with care.`);
    sqft = null; // keep the record visible, drop the math
  }

  let coolingTons: number | null = null;
  let heatInputKbtu: number | null = null;

  if (sqft != null && sqft > 0) {
    // cooling: era-adjusted sqft/ton, to the nearest half ton, 1.5–5 band
    const rawTons = sqft / coolSqftPerTon(year);
    coolingTons = Math.min(5, Math.max(1.5, Math.round(rawTons * 2) / 2));
    if (rawTons > 5.25) caution("Over 5 tons", "Math says more than 5 tons — big house. Two systems or zoning may be in play; load calc essential.");

    // heating: era output need → smallest standard input bucket that covers it at ~95% AFUE
    const needK = (sqft * heatFactor(year)) / 1000;
    heatInputKbtu = FURNACE_BUCKETS.find((b) => b * 0.95 >= needK) ?? 120;
    if (needK > 114) caution("Over 120k BTU", "Heat load tops the 120k bucket — verify with the load calc before quoting.");

    reasons.push(
      `${sqft.toLocaleString()} finished sq ft` +
        (year ? `, built ${year}` : "") +
        ` → ~${coolingTons} ton cooling, ~${heatInputKbtu}k BTU furnace (MN -13°F design).`,
    );
  }

  const exC = existing.coolingTons;
  const exH = existing.heatInputKbtu;

  // Existing = upper bound. Never suggest bigger without saying it.
  let coolAgrees = false, heatAgrees = false;
  if (coolingTons != null && exC != null) {
    if (coolingTons > exC) {
      coolingTons = exC;
      caution(`Held at existing ${exC} ton`, `Existing A/C is ${exC} ton — matching it. Old units ran oversized; don't go bigger without a Manual J.`);
    } else if (coolingTons < exC) {
      reasons.push(`Existing A/C is ${exC} ton — the math says ${coolingTons}. Oversizing was common; the smaller size is likely right.`);
    } else {
      coolAgrees = true;
      reasons.push(`Existing A/C (${exC} ton) agrees with the math.`);
    }
  } else if (coolingTons == null && exC != null) {
    coolingTons = exC;
    reasons.push(`No county match for cooling math — showing the existing unit's size (${exC} ton${existing.coolingModel ? `, ${existing.coolingModel}` : ""}).`);
  }

  if (heatInputKbtu != null && exH != null) {
    if (heatInputKbtu > exH) {
      heatInputKbtu = exH;
      caution(`Held at existing ${exH}k`, `Existing furnace is ${exH}k input — matching it rather than upsizing without a load calc.`);
    } else if (Math.abs(heatInputKbtu - exH) <= 10) {
      heatAgrees = true;
      reasons.push(`Existing furnace (${exH}k input) agrees with the math.`);
    } else {
      reasons.push(`Existing furnace is ${exH}k input — the math says ${heatInputKbtu}k. Older installs often oversized.`);
    }
  } else if (heatInputKbtu == null && exH != null) {
    heatInputKbtu = exH;
    reasons.push(`No county match for heating math — showing the existing furnace's input (${exH}k${existing.heatModel ? `, ${existing.heatModel}` : ""}).`);
  }

  if (coolingTons == null && heatInputKbtu == null) return null;

  // solid = two sources that AGREE on at least one family; anything
  // single-sourced stays rough.
  const confidence: "solid" | "rough" = coolAgrees || heatAgrees ? "solid" : "rough";

  return {
    coolingTons,
    heatInputKbtu,
    confidence,
    reasons,
    flags: cautions.map((c) => c.detail),
    cautions,
    inputs: {
      sqft,
      yearBuilt: year,
      parcelMatched: parcel != null,
      existingCoolingTons: exC,
      existingHeatInputKbtu: exH,
    },
  };
}

/**
 * Picker helper — does this catalog item's model match the suggestion for its
 * slot? Cooling matches the exact half-ton step; heating matches within 15%
 * of the suggested input (brands step 66/70/80/88/90 around our buckets).
 */
export function isRightSize(model: string | null | undefined, sizing: SizingSuggestion | null, hint: SizeFamily | null): boolean {
  if (!sizing || !hint) return false;
  const d = decodeEquipSize(model, hint);
  if (!d) return false;
  if (hint === "cooling" && d.tons != null && sizing.coolingTons != null) {
    return Math.abs(d.tons - sizing.coolingTons) <= 0.26;
  }
  if (hint === "heating" && d.inputKbtu != null && sizing.heatInputKbtu != null) {
    return Math.abs(d.inputKbtu - sizing.heatInputKbtu) <= sizing.heatInputKbtu * 0.15;
  }
  return false;
}
